import type { Filters, GroupedQuotes, ProviderQuote, LabelType } from '../types'

function getSpotlightProviders(groupedQuotes: GroupedQuotes[]): number[] {
  let maxExposure = 0
  let indexes = []
  for (let index = 0; index < groupedQuotes.length; index++) {
    const exposure = groupedQuotes[index].exposure
    if (exposure && exposure > maxExposure) {
      maxExposure = exposure
      indexes = []
    }
    if (exposure === maxExposure) {
      indexes.push(index)
    }
  }
  return indexes
}

function getBestRated(groupedQuotes: GroupedQuotes[]) {
  let maxScore = 0
  let bestRatedIndex: number | undefined
  for (let index = 0; index < groupedQuotes.length; index++) {
    const score = groupedQuotes[index].psp.score?.value
    if (score && score > maxScore) {
      maxScore = score
      bestRatedIndex = index
    }
  }
  return bestRatedIndex
}

function getBestCategoryScore(groupedQuotes: GroupedQuotes[], category: string) {
  let maxScore = 0
  let bestIndex: number | undefined
  for (let index = 0; index < groupedQuotes.length; index++) {
    const categories = groupedQuotes[index].psp.score?.categories
    const score = categories?.find((c) => c.name === category)?.value
    if (score && score > maxScore) {
      maxScore = score
      bestIndex = index
    }
  }
  return bestIndex
}

function getFastestTime(provider: GroupedQuotes) {
  return provider.services.reduce((bestTime, { transferTime }) => {
    const time = (transferTime.min + transferTime.max) / 2
    return time < bestTime ? time : bestTime
  }, Number.MAX_SAFE_INTEGER)
}

function getFastest(groupedQuotes: GroupedQuotes[]) {
  let fastestTime = Number.MAX_SAFE_INTEGER
  let fastestIndex: number | undefined
  for (let index = 0; index < groupedQuotes.length; index++) {
    const time = getFastestTime(groupedQuotes[index])
    if (time < fastestTime) {
      fastestTime = time
      fastestIndex = index
    }
  }
  return fastestIndex
}

export function getSpotlightInfo(
  groupedQuotes: GroupedQuotes[],
  cheapestOver3Months: { provider: { slug: string }; percentage: number } | null,
): { index: number; label: LabelType; percentage?: number | null } | null {
  const indexes = getSpotlightProviders(groupedQuotes)
  const cheapestEI = groupedQuotes[0]?.exposure ?? 0
  const highestEI = groupedQuotes[indexes[0]]?.exposure ?? 0

  if (indexes.length && highestEI > 0 && highestEI > 1.3 * cheapestEI) {
    const cheapestOver3MonthsIndex = groupedQuotes.findIndex(
      (gq) => gq.psp.slug === cheapestOver3Months?.provider.slug,
    )
    if (cheapestOver3MonthsIndex > -1 && indexes.includes(cheapestOver3MonthsIndex)) {
      return {
        index: cheapestOver3MonthsIndex,
        label: 'best_for_regular_transfers',
        percentage: cheapestOver3Months?.percentage,
      }
    }

    const bestRatedIndex = getBestRated(groupedQuotes)
    if (bestRatedIndex && indexes.includes(bestRatedIndex)) {
      return { index: bestRatedIndex, label: 'best_rated' }
    }
    const fastestIndex = getFastest(groupedQuotes)
    if (fastestIndex && indexes.includes(fastestIndex)) {
      return { index: fastestIndex, label: 'fastest' }
    }
    const bestCustomerRatedIndex = getBestCategoryScore(groupedQuotes, 'customer')
    if (bestCustomerRatedIndex && indexes.includes(bestCustomerRatedIndex)) {
      return { index: bestCustomerRatedIndex, label: 'best_customer_ratings' }
    }
    const mostTrustedIndex = getBestCategoryScore(groupedQuotes, 'trust')
    if (mostTrustedIndex && indexes.includes(mostTrustedIndex)) {
      return { index: mostTrustedIndex, label: 'best_trust_score' }
    }
    return { index: indexes[0], label: 'sponsored' }
  }
  return null
}

function filterExposures(quotes: ProviderQuote['quotes'], filters: Filters) {
  const exposures = quotes.flatMap((q) => q.exposures ?? [])
  return exposures.filter(
    (e) =>
      (!e.device || e.device === filters.device) &&
      (!e.language || e.language.includes(filters.lang)),
  )
}

export function providerHasNegativeExposure(quotes: ProviderQuote['quotes'], filters: Filters) {
  const filtered = filterExposures(quotes, filters)
  return filtered.length > 0 && filtered[filtered.length - 1].index < 0
}

export function providerHasPositiveExposure(quotes: ProviderQuote['quotes'], filters: Filters) {
  const filtered = filterExposures(quotes, filters)
  return filtered.length > 0 && filtered[filtered.length - 1].index > 0
}

export function getProviderExposure({ psp, quotes }: ProviderQuote, filters: Filters) {
  let maxExposure: number | null = null
  const filtered = filterExposures(quotes, filters)
  if (
    filtered.length &&
    filtered[filtered.length - 1] &&
    Math.abs(filtered[filtered.length - 1].index) > Number(maxExposure)
  ) {
    maxExposure = Math.abs(filtered[filtered.length - 1].index)
  }
  if (maxExposure !== null && filters.usedProviders?.includes(psp.slug)) {
    return maxExposure - 100000
  }
  return maxExposure
}
